'use client'

import { useState } from 'react'
import { ScoringMethodologyNote } from '@/components/radar/ScoringMethodologyNote'
import {
  Bot,
  FileText,
  Code2,
  BookOpen,
  Layout,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  Mail,
  Check,
  Loader2,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import OverallScore from './OverallScore'
import BotAccessGrid from './BotAccessGrid'
import ScoreCard from './ScoreCard'
import type { Finding } from './ScoreCard'
import PlatformCTA from '@/components/platform/PlatformCTA'
import { isCrawlRetrievalBot } from '@/lib/tools/crawl-bots'

// Result type matching the API response
interface CrawlCheckResult {
  url: string
  checkedAt: string
  /** null when the grade was withheld (scan too blocked to grade) */
  overallScore: number | null
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null
  /** optional: saved reports from before the withheld-grade fix lack it */
  scoreStatus?: 'graded' | 'withheld'
  scanCompleteness: number
  /** Older saved reports predate the server's recorded rubric. */
  scoreBreakdown?: {
    category: string
    score: number
    maxScore: number
    verifiable: boolean
  }[]
  pageBlocked: boolean
  botAccess: {
    bot: string
    category: 'search' | 'ai-browse' | 'ai-train' | 'seo'
    allowed: boolean
    robotsTxt: 'allowed' | 'blocked' | 'no-rule'
    httpStatus: number
    responseTime: number
    xRobotsTag: string | null
  }[]
  robotsTxt: {
    exists: boolean
    blocked: boolean
    /** Optional: saved results from before 2026-07-20 do not carry it. */
    accessFailed?: boolean
    hasSitemap: boolean
    hasCrawlDelay: boolean
    rawContent: string
  }
  metaRobots: {
    global: string | null
    botSpecific: { bot: string; content: string }[]
  }
  structuredData: {
    found: boolean
    types: string[]
    count: number
    hasArticle: boolean
    hasFaq: boolean
    hasOrganization: boolean
    hasProduct: boolean
    hasBreadcrumb: boolean
  }
  llmsTxt: {
    exists: boolean
    blocked: boolean
    /** Optional: saved results from before 2026-07-20 do not carry it. */
    accessFailed?: boolean
    score: number
    hasSections: boolean
    hasEntityDefinitions: boolean
    hasUrls: boolean
    hasUsePolicy: boolean
    wordCount: number
  }
  contentAccess: {
    hasContent: boolean
    titleLength: number
    descriptionLength: number
    framework: string | null
    isServerRendered: boolean
  }
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

interface CrawlResultsProps {
  result: CrawlCheckResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (the email-share row, `PlatformCTA`, and the gradient
   * services CTA) so the SAME rich report can be reused inside `/platform/app`.
   * Result content (score ring, partial-scan banner, AI analysis, bot-access
   * grid with WAF/403 badges, the four category score cards, recommendations
   * with blog links) is identical either way.
   */
  variant?: 'public' | 'dashboard'
}

const BLOG_LINKS: Record<string, string> = {
  'llms.txt': '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  'Structured Data': '/blogs/knowledge-graph-llm-visibility-real-data',
  'Bot Access': '/blogs/build-brand-ai-search-engines-cite',
}

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

export default function CrawlResults({
  result,
  variant = 'public',
}: CrawlResultsProps) {
  // Grade withheld: the scan was too blocked to grade honestly. Old saved
  // reports predate scoreStatus, so a null score is treated the same way.
  const withheld =
    result.scoreStatus === 'withheld' || result.overallScore === null
  // "Blocked" now means "no usable file came back", which INCLUDES a soft-404
  // where the file is measurably absent. Only `accessFailed` means the server
  // refused or never answered, so the CDN/WAF messaging keys off that.
  // Older saved results predate the flag: fall back to `blocked` so they keep
  // rendering exactly as they did when they were produced.
  const robotsAccessFailed =
    result.robotsTxt.accessFailed ?? result.robotsTxt.blocked
  const llmsAccessFailed = result.llmsTxt.accessFailed ?? result.llmsTxt.blocked

  // Zero verified policy signals — bot "allowed" flags are defaults, not data.
  const botUnverified = robotsAccessFailed && result.pageBlocked

  // --- Compute category scores ---
  const botCardScore = (() => {
    if (Array.isArray(result.scoreBreakdown)) {
      const categories = result.scoreBreakdown.filter(
        item =>
          item != null &&
          (item.category === 'Bot Access' ||
            item.category === 'robots.txt Rules') &&
          Number.isFinite(item.score) &&
          Number.isFinite(item.maxScore) &&
          item.score >= 0 &&
          item.maxScore > 0 &&
          item.score <= item.maxScore &&
          (item.verifiable === undefined ||
            typeof item.verifiable === 'boolean')
      )
      if (
        categories.some(item => item.category === 'Bot Access') &&
        categories.some(item => item.category === 'robots.txt Rules')
      ) {
        return {
          score: categories.reduce(
            (sum, item) => sum + (item.verifiable === false ? 0 : item.score),
            0
          ),
          max: categories.reduce((sum, item) => sum + item.maxScore, 0),
        }
      }
    }

    // Legacy reports have no recorded breakdown. Keep their original roster
    // calculation rather than rescoring them with today's retrieval pool.
    if (botUnverified) return { score: 0, max: 40 }
    let s = 0
    const aiBrowse = result.botAccess.filter(b => b.category === 'ai-browse')
    const search = result.botAccess.filter(b => b.category === 'search')
    const train = result.botAccess.filter(b => b.category === 'ai-train')
    s += aiBrowse.filter(b => b.allowed).length * 3
    s += search.filter(b => b.allowed).length * 3
    if (train.filter(b => b.robotsTxt !== 'no-rule').length >= 3) s += 2
    if (result.robotsTxt.hasSitemap) s += 2
    if (result.robotsTxt.hasCrawlDelay) s += 2
    if (result.robotsTxt.exists) s += 8
    return { score: Math.min(s, 40), max: 40 }
  })()

  const sdScore = (() => {
    let s = 0
    if (result.structuredData.found) s += 5
    // Primary schema: Organization OR Article (page-type aware)
    if (
      result.structuredData.hasOrganization ||
      result.structuredData.hasArticle
    )
      s += 10
    // Navigational: BreadcrumbList, Product, or WebSite
    if (
      result.structuredData.hasBreadcrumb ||
      result.structuredData.hasProduct ||
      result.structuredData.types.includes('WebSite')
    )
      s += 5
    // Rich bonus: FAQPage, HowTo, or both Organization + Article
    if (
      result.structuredData.hasFaq ||
      result.structuredData.types.includes('HowTo') ||
      (result.structuredData.hasOrganization &&
        result.structuredData.hasArticle)
    )
      s += 5
    return s
  })()

  const contentScore = (() => {
    let s = 0
    if (result.contentAccess.isServerRendered) s += 5
    if (
      result.contentAccess.titleLength >= 30 &&
      result.contentAccess.titleLength <= 70
    )
      s += 5
    if (
      result.contentAccess.descriptionLength >= 100 &&
      result.contentAccess.descriptionLength <= 170
    )
      s += 5
    return s
  })()

  // --- Email share state ---
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [shareError, setShareError] = useState('')

  async function handleEmailShare() {
    if (
      !shareEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
    ) {
      setShareError('Please enter a valid email address.')
      setShareStatus('error')
      return
    }

    setShareStatus('sending')
    setShareError('')

    try {
      const res = await fetch('/api/tools/crawl-check/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          url: result.url,
          overallScore: result.overallScore,
          grade: result.grade,
          botAccessScore: botCardScore,
          structuredDataScore: { score: sdScore, max: 25 },
          llmsTxtScore: {
            score: Math.round(result.llmsTxt.score / 5),
            max: 20,
          },
          contentScore: { score: contentScore, max: 15 },
          recommendations: result.recommendations,
          aiSummary: result.aiSummary?.summary || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to send email.')
      }

      setShareStatus('sent')
      setTimeout(() => {
        setShareStatus('idle')
        setShowEmailInput(false)
        setShareEmail('')
      }, 2000)
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : 'Failed to send email.'
      )
      setShareStatus('error')
    }
  }

  // --- Compute findings ---
  const botFindings: Finding[] = []
  if (botUnverified) {
    botFindings.push({
      text: 'Bot access could not be verified (robots.txt and page blocked by CDN/WAF)',
      status: 'neutral',
    })
  } else {
    const allowedAI = result.botAccess.filter(
      b => isCrawlRetrievalBot(b.bot) && b.allowed
    ).length
    const totalAI = result.botAccess.filter(b =>
      isCrawlRetrievalBot(b.bot)
    ).length
    botFindings.push(
      totalAI > 0
        ? {
            text: `${allowedAI}/${totalAI} AI retrieval bots can access your site`,
            status: allowedAI >= totalAI / 2 ? 'good' : 'bad',
          }
        : {
            text: 'No current AI retrieval agents were tested in this report',
            status: 'neutral',
          }
    )
    const allowedSearch = result.botAccess.filter(
      b => b.category === 'search' && b.allowed
    ).length
    botFindings.push({
      text: `${allowedSearch}/3 search engine bots allowed`,
      status: allowedSearch >= 2 ? 'good' : 'bad',
    })
  }
  if (robotsAccessFailed) {
    botFindings.push({
      text: 'robots.txt unreachable (blocked by CDN/WAF)',
      status: 'neutral',
    })
  } else if (result.robotsTxt.blocked) {
    // Soft-404: the server answered, just not with a robots.txt.
    botFindings.push({ text: 'No robots.txt found', status: 'bad' })
  } else if (result.robotsTxt.exists) {
    botFindings.push({ text: 'robots.txt found', status: 'good' })
    botFindings.push(
      result.robotsTxt.hasSitemap
        ? { text: 'Sitemap directive present', status: 'good' }
        : { text: 'No Sitemap directive', status: 'bad' }
    )
  } else {
    botFindings.push({ text: 'No robots.txt found', status: 'bad' })
  }

  const sdFindings: Finding[] = []
  if (result.pageBlocked && !result.structuredData.found) {
    sdFindings.push({
      text: 'Unable to verify (page blocked by CDN/WAF)',
      status: 'neutral',
    })
  } else if (result.structuredData.found) {
    sdFindings.push({
      text: `${result.structuredData.count} JSON-LD block${result.structuredData.count !== 1 ? 's' : ''} found`,
      status: 'good',
    })
    if (result.structuredData.types.length > 0) {
      sdFindings.push({
        text: `Types: ${result.structuredData.types.join(', ')}`,
        status: 'neutral',
      })
    }
  } else {
    sdFindings.push({
      text: 'No JSON-LD structured data detected',
      status: 'bad',
    })
  }

  const llmsFindings: Finding[] = []
  if (llmsAccessFailed) {
    llmsFindings.push({
      text: 'Unable to verify (blocked by CDN/WAF)',
      status: 'neutral',
    })
  } else if (result.llmsTxt.blocked) {
    // Soft-404: measurably absent, which is what the score now reflects.
    llmsFindings.push({ text: 'No llms.txt found', status: 'bad' })
  } else if (result.llmsTxt.exists) {
    llmsFindings.push({
      text: `${result.llmsTxt.wordCount} words`,
      status: 'neutral',
    })
    if (result.llmsTxt.hasSections)
      llmsFindings.push({ text: 'Has section headings', status: 'good' })
    if (result.llmsTxt.hasUrls)
      llmsFindings.push({ text: 'Contains URLs', status: 'good' })
    if (result.llmsTxt.hasUsePolicy)
      llmsFindings.push({ text: 'Use policy detected', status: 'good' })
    if (!result.llmsTxt.hasSections && !result.llmsTxt.hasUrls)
      llmsFindings.push({ text: 'Minimal structure', status: 'bad' })
  } else {
    llmsFindings.push({ text: 'No llms.txt file found', status: 'bad' })
    llmsFindings.push({
      text: 'Missing opportunity for AI discovery',
      status: 'bad',
    })
  }

  const contentFindings: Finding[] = []
  if (result.pageBlocked) {
    contentFindings.push({
      text: 'Unable to verify (page blocked by CDN/WAF)',
      status: 'neutral',
    })
  } else {
    if (result.contentAccess.framework)
      contentFindings.push({
        text: `Framework: ${result.contentAccess.framework}`,
        status: 'neutral',
      })
    contentFindings.push(
      result.contentAccess.isServerRendered
        ? { text: 'Server-rendered content', status: 'good' }
        : { text: 'Client-only rendering detected', status: 'bad' }
    )
    const titleLen = result.contentAccess.titleLength
    contentFindings.push({
      text: `Title: ${titleLen} chars`,
      status: titleLen >= 30 && titleLen <= 70 ? 'good' : 'bad',
    })
    const descLen = result.contentAccess.descriptionLength
    contentFindings.push({
      text: `Description: ${descLen} chars`,
      status: descLen >= 100 && descLen <= 170 ? 'good' : 'bad',
    })
  }

  return (
    <div className='space-y-8'>
      {/* Scoring change explained where the score is read, not in a changelog */}
      <ScoringMethodologyNote toolSlug='crawl-check' />

      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>Results for</p>
            <p className='text-sm font-medium text-foreground truncate max-w-lg mx-auto'>
              {result.url}
            </p>
          </div>
          {withheld ? (
            <div className='flex flex-col items-center gap-4 py-8 text-center'>
              <AlertTriangle size={48} style={{ color: '#FDC304' }} />
              <p className='text-2xl font-bold text-foreground'>
                Grade withheld
              </p>
              <p className='text-sm text-foreground/80 max-w-md leading-relaxed'>
                Your server (CDN/WAF) blocked most of our checks, so there is
                not enough verified data to grade this site honestly. This is
                not a pass or a fail — it is an incomplete scan. Allowlist our
                checker or relax the bot challenge, then run it again for a real
                score.
              </p>
            </div>
          ) : (
            <OverallScore
              score={result.overallScore as number}
              grade={result.grade as 'A' | 'B' | 'C' | 'D' | 'F'}
            />
          )}
          {!withheld && result.scanCompleteness < 100 && (
            <div
              className='mt-3 rounded-lg px-4 py-2.5 text-xs text-foreground/80 leading-relaxed text-center'
              style={{
                backgroundColor: '#FDC30410',
                border: '1px solid #FDC30420',
              }}
            >
              <strong style={{ color: '#FDC304' }}>Partial scan:</strong> Only{' '}
              {result.scanCompleteness}% of checks could complete. Some
              resources were blocked by the site&apos;s CDN/WAF. Score reflects
              only verifiable sections.
            </div>
          )}
        </div>
      </LenisFadeIn>

      {/* Email share — public surface only; the share email is built around
          the score, so a withheld scan has nothing truthful to send */}
      {variant !== 'dashboard' && !withheld && (
        <div className='flex flex-col items-center gap-2'>
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              className='inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors'
            >
              <Mail size={16} />
              Email results
            </button>
          ) : (
            <div className='flex items-center gap-2 w-full max-w-md'>
              <input
                type='email'
                value={shareEmail}
                onChange={e => {
                  setShareEmail(e.target.value)
                  if (shareStatus === 'error') setShareStatus('idle')
                }}
                placeholder='colleague@company.com'
                className='flex-1 h-10 px-4 text-sm rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40'
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailShare()
                }}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                autoFocus
              />
              <button
                onClick={handleEmailShare}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                className='inline-flex items-center gap-2 text-sm font-medium h-10 px-5 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors disabled:opacity-50'
              >
                {shareStatus === 'sending' ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : shareStatus === 'sent' ? (
                  <>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e' }}>Sent!</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
          {shareStatus === 'error' && shareError && (
            <p className='text-xs' style={{ color: '#F90B8A' }}>
              {shareError}
            </p>
          )}
        </div>
      )}

      {/* Platform CTA — public surface only */}
      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='Crawl Check'
          score={result.overallScore ?? undefined}
          grade={result.grade ?? undefined}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Bot Access Grid */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <h2 className='text-lg font-semibold text-foreground mb-2 flex items-center gap-2'>
            <Bot size={20} style={{ color: '#8b5cf6' }} />
            Bot Access Results
          </h2>
          <p className='text-xs text-muted-foreground mb-4'>
            Access status is based on robots.txt rules, meta robots tags, and
            X-Robots-Tag headers. HTTP status is a reachability check from our
            server. A &quot;WAF&quot; badge means the site&apos;s firewall (e.g.
            Cloudflare) blocked our test request, but the policy allows the bot.
            Real bots from verified IP ranges are typically whitelisted by these
            firewalls.
          </p>
          <BotAccessGrid bots={result.botAccess} />
        </div>
      </LenisFadeIn>

      {/* Category Score Cards */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <ScoreCard
            title='Bot Access & robots.txt'
            icon={<FileText size={18} />}
            score={botCardScore.score}
            maxScore={botCardScore.max}
            findings={botFindings}
            color='#008dcc'
            details={
              result.robotsTxt.rawContent ? (
                <pre className='whitespace-pre-wrap font-mono text-[11px]'>
                  {result.robotsTxt.rawContent}
                </pre>
              ) : undefined
            }
          />

          <ScoreCard
            title='Structured Data'
            icon={<Code2 size={18} />}
            score={sdScore}
            maxScore={25}
            findings={sdFindings}
            color='#8b5cf6'
          />

          <ScoreCard
            title='llms.txt'
            icon={<BookOpen size={18} />}
            score={Math.round(result.llmsTxt.score / 5)}
            maxScore={20}
            findings={llmsFindings}
            color='#FDC304'
          />

          <ScoreCard
            title='Content Quality'
            icon={<Layout size={18} />}
            score={contentScore}
            maxScore={15}
            findings={contentFindings}
            color='#22c55e'
          />
        </div>
      </LenisStaggered>

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Recommendations
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                const blogLink = BLOG_LINKS[rec.category]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                      {blogLink && (
                        <a
                          href={blogLink}
                          className='inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5'
                        >
                          Learn more <ArrowRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #8c1b4c, #b32a6c, #e54b8c)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Want a deeper analysis?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              Our AI visibility audit covers knowledge graph mapping, entity
              resolution, citation tracking, and a custom optimization roadmap.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#b32a6c'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Strategy Session
              </a>
              <a
                href='/platform'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Or DIY with Radar (free){' '}
                <ArrowRight size={16} className='ml-1' />
              </a>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
